/*  PaymentPendingDialog — shown when a doctor tries to enroll in a paid
    course before the payment gateway is wired.
    ──────────────────────────────────────────────────────────────────────
    Renders a friendly Contact-CoE modal with mailto + WhatsApp CTAs so the
    CoE team can onboard paying doctors manually. Once Stripe/Razorpay is
    live, the parent will swap this dialog out for a real checkout redirect.
*/
import React from "react";
import { EnvelopeSimple, WhatsappLogo, Info, X } from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";


const SUPPORT_EMAIL = "academy@invisique.com";
const SUPPORT_WHATSAPP = "+919000000000"; // TODO: replace with real CoE number when provided


function formatINR(rupees) {
    if (rupees == null) return "";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(rupees);
}


export default function PaymentPendingDialog({ open, onOpenChange, courseTitle, priceRupees }) {
    const priceLabel = priceRupees ? `₹${formatINR(priceRupees)}` : "the listed price";
    const mailSubject = encodeURIComponent(`Enrollment request — ${courseTitle || "Course"}`);
    const mailBody = encodeURIComponent(
        `Hi Invisique CoE team,\n\nI'd like to enroll in "${courseTitle || "the course"}" (${priceLabel}). Please share payment details.\n\nThanks.`,
    );
    const waText = encodeURIComponent(
        `Hi Invisique CoE, I'd like to enroll in "${courseTitle || "a paid course"}" (${priceLabel}). Please share payment details.`,
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="payment-pending-dialog">
                <DialogHeader>
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 w-9 h-9 rounded-xl grid place-items-center bg-primary/10 text-primary ring-1 ring-primary/20">
                            <Info size={18} weight="duotone" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <DialogTitle className="font-display text-base leading-tight">
                                Enroll via CoE Support
                            </DialogTitle>
                            <DialogDescription className="mt-1 text-[12.5px] leading-snug text-muted-foreground">
                                <span className="font-semibold text-foreground">{courseTitle || "This course"}</span> is a paid enrollment ({priceLabel}). Our checkout gateway is being finalized — until then, our CoE team will onboard you personally within one business day.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="mt-3 space-y-2">
                    <a
                        href={`mailto:${SUPPORT_EMAIL}?subject=${mailSubject}&body=${mailBody}`}
                        className="flex items-center gap-3 rounded-lg border border-border bg-card hover:bg-secondary/50 px-3 py-2.5 transition-colors no-underline"
                        data-testid="payment-pending-email-btn"
                    >
                        <EnvelopeSimple size={18} weight="duotone" className="text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-foreground">Email us</div>
                            <div className="text-[11px] text-muted-foreground truncate">{SUPPORT_EMAIL}</div>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Open</span>
                    </a>
                    <a
                        href={`https://wa.me/${SUPPORT_WHATSAPP.replace(/[^\d]/g, "")}?text=${waText}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 rounded-lg border border-border bg-card hover:bg-secondary/50 px-3 py-2.5 transition-colors no-underline"
                        data-testid="payment-pending-whatsapp-btn"
                    >
                        <WhatsappLogo size={18} weight="duotone" className="text-emerald-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-foreground">WhatsApp CoE</div>
                            <div className="text-[11px] text-muted-foreground truncate">{SUPPORT_WHATSAPP}</div>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Chat</span>
                    </a>
                </div>

                <div className="mt-3 flex items-center justify-end">
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-border bg-background text-xs font-semibold hover:bg-secondary transition-colors"
                        data-testid="payment-pending-close-btn"
                    >
                        <X size={11} weight="bold" /> Close
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
